import React from 'react';

import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlugCircleBolt } from '@fortawesome/pro-regular-svg-icons';

import { Button } from 'components/ui/button';

import { buildReauthUrl } from './helpers';

import type { ProviderName } from 'components/nylas/v3/types';

interface Props {
  connected: boolean;
  providerName?: ProviderName;
  authPath: string;
  contactId: number;
  onClose: () => void;
}

const MAILBOX_LABELS: Record<ProviderName, string> = { google: 'Gmail', microsoft: 'Outlook' };
const PROVIDERS: ProviderName[] = ['google', 'microsoft'];

export default function ReauthModal({ connected, providerName, authPath, contactId, onClose }: Props) {
  const reconnectProvider = connected ? providerName : undefined;

  const startReauth = (provider: ProviderName) => {
    window.location.assign(buildReauthUrl(authPath, provider, contactId).toString());
  };

  return (
    <Modal open onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon className="text-primary" icon={faPlugCircleBolt} />
            {reconnectProvider ? 'Reconnect your email to send' : 'Connect your email to send'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-0">
            {reconnectProvider
              ? `Sending email needs a permission your ${MAILBOX_LABELS[reconnectProvider]} connection doesn't ` +
                'have yet. Reconnect it to grant send access — you\'ll come right back here to finish your email.'
              : 'Connect your email account to send from here. You\'ll grant send permission once and come ' +
                'right back to finish your email.'}
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button label="Cancel" variant="ghost" onClick={onClose} />
          {reconnectProvider ? (
            <Button onClick={() => startReauth(reconnectProvider)}>
              Reconnect {MAILBOX_LABELS[reconnectProvider]}
            </Button>
          ) : (
            PROVIDERS.map(provider => (
              <Button key={provider} variant="outline" onClick={() => startReauth(provider)}>
                Sign in with {MAILBOX_LABELS[provider]}
              </Button>
            ))
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
